import { useState, useEffect } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  type Timestamp,
} from "firebase/firestore";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useIsSystemAdmin } from "@/hooks/useIsSystemAdmin";
import { logError } from "@/shared/services/ErrorReporting";
import { toast } from "@/utils/toast";
import { db } from "@/integrations/firebase/firestore";

export interface ITenant {
  tenantId: string;
  name: string;
  status: string;
  adminUids: string[];
  memberCount: number;
}

export interface AssignmentHistoryEntry {
  action: "added" | "removed";
  email: string;
  date: Timestamp;
}

export const useTenants = () => {
  const { t } = useTranslation(["admin"]);
  const { authUser } = useAuth();
  const { isSystemAdmin, isLoading: isAdminLoading } = useIsSystemAdmin();
  const [tenants, setTenants] = useState<ITenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!authUser || isAdminLoading) {
      setTenants([]);
      setIsLoading(false);
      return;
    }

    const tenantsRef = collection(db, "tenants");

    const q = isSystemAdmin
      ? query(tenantsRef, orderBy("name"))
      : query(tenantsRef, where("admin_uids", "array-contains", authUser.uid));

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const tenantsData: ITenant[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          tenantsData.push({
            tenantId: doc.id,
            name: data.name,
            status: data.status,
            adminUids: data.admin_uids ?? [],
            memberCount: (data.active_user_emails ?? []).length,
          });
        });
        setTenants(tenantsData);
        setError(null);
        setIsLoading(false);
      },
      (err) => {
        logError(err);
        toast.error(t("admin:tenants.errorLoadingTenants"));
        setError(err);
        setIsLoading(false);
      },
    );

    return () => unsubscribe();
  }, [authUser, isSystemAdmin, isAdminLoading, t]);

  return { tenants, isLoading: isLoading || isAdminLoading, error };
};
